import { supabase } from '../../lib/supabaseClient';
import type { Tables } from '../../types/database.types';

export type Notification = Tables<'notifications'>;

export type NotificationType =
  | 'ticket_assigned'
  | 'ticket_delegated'
  | 'ticket_queue_alert'
  | 'ticket_unassigned'
  | 'ticket_status_changed'
  | 'ticket_note_added'
  | 'mention'
  | 'dm'
  | 'system'
  | 'task_reminder'
  | 'task_assigned'
  | 'dossier_file_added'
  | 'dossier_email_attached'
  | 'dossier_stage_changed'
  | 'dossier_hearing_set'
  | 'dossier_member_added'
  | 'dossier_member_removed'
  | 'hearing_reminder'
  | 'prescription_reminder';

export interface NotificationMetadata {
  ticket_id?: string;
  category?: string;
  priority?: string;
  reason?: string;
  actor_id?: string;
  actor_name?: string;
  from_status?: string;
  to_status?: string;
  note_preview?: string;
  [key: string]: unknown;
}

export async function getNotifications(limit = 50): Promise<Notification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data ?? [];
}

export async function getUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('is_read', false);

  if (error) throw error;
  return count ?? 0;
}

export async function markAsRead(id: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function markAllAsRead(): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('is_read', false);
  if (error) throw error;
}

export async function deleteNotification(id: string): Promise<void> {
  const { error } = await supabase.from('notifications').delete().eq('id', id);
  if (error) throw error;
}

export async function clearAllNotifications(): Promise<void> {
  const { error } = await supabase.from('notifications').delete().neq('id', '00000000-0000-0000-0000-000000000000');
  if (error) throw error;
}

export interface CreateNotificationInput {
  recipient_id: string;
  type: NotificationType;
  title: string;
  message: string;
  metadata?: NotificationMetadata;
}

export async function createNotification(input: CreateNotificationInput): Promise<string | null> {
  const { data, error } = await supabase.rpc('create_notification', {
    p_recipient_id: input.recipient_id,
    p_type: input.type,
    p_title: input.title,
    p_message: input.message,
    p_metadata: (input.metadata ?? {}) as never,
  });
  if (error) {
    console.error('[createNotification] RPC failed:', error);
    return null;
  }
  return data ?? null;
}

// Fire-and-forget helper: create several notifications without blocking the caller
export async function notifyMany(inputs: CreateNotificationInput[]): Promise<void> {
  const unique = new Map<string, CreateNotificationInput>();
  for (const input of inputs) {
    if (input.recipient_id) unique.set(input.recipient_id, input);
  }
  await Promise.all(
    Array.from(unique.values()).map((input) =>
      createNotification(input).catch((err) => {
        console.error('[notifyMany] failed:', err);
        return null;
      }),
    ),
  );
}
